import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { LogOut, GraduationCap, KeyRound, MessageCircleQuestion, MessageSquare } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { AskSomeoneModal } from "@/components/AskSomeoneModal";
import { MessagesDrawer } from "@/components/MessagesDrawer";
import { api } from "@/lib/api";

export function TopBar({ subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [showMsgs, setShowMsgs] = useState(false);
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(0);

  const chime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.35);
    } catch (e) { console.debug("notification chime unavailable:", e); }
  };

  const pollUnread = async () => {
    try {
      const { data } = await api.get("/conversations");
      const total = data.unread_total || 0;
      if (total > prevUnread.current) chime();
      prevUnread.current = total;
      setUnread(total);
    } catch (e) { console.debug("unread poll failed:", e); }
  };
  useEffect(() => {
    if (!user) return;
    pollUnread();
    const t = setInterval(pollUnread, 12000);
    return () => clearInterval(t);
  }, [user, showMsgs]); // eslint-disable-line

  const accent = user?.role === "Parent" ? "#E07A5F" : "#81B29A";
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-[#EAE6DF]">
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
        <button
          data-testid="nav-home-btn"
          onClick={() => navigate(user?.role === "Parent" ? "/parent" : "/student")}
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center w-10 h-10 rounded-2xl text-white" style={{ background: accent }}>
            <GraduationCap size={20} />
          </span>
          <span className="text-left leading-tight">
            <span className="block font-head font-bold text-lg text-[#3D405B]">Rising Force Academy</span>
            <span className="block text-xs text-[#6B7280]">{subtitle}</span>
          </span>
        </button>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-sm text-[#6B7280]">
            {user?.first_name} · <span className="text-[#3D405B] font-medium">{user?.title || user?.role}</span>
          </span>
          <button
            data-testid="messages-btn"
            onClick={() => setShowMsgs(true)}
            className="relative flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] transition-colors"
          >
            <MessageSquare size={18} /> <span className="hidden sm:inline">Messages</span>
            {unread > 0 && <span data-testid="unread-badge" className="absolute -top-2 -right-2 stat text-[10px] bg-[#E07A5F] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{unread}</span>}
          </button>
          <button
            data-testid="ask-someone-btn"
            onClick={() => setShowAsk(true)}
            className="flex items-center gap-2 text-sm font-medium rounded-full px-3 py-1.5 text-white transition-transform hover:scale-105"
            style={{ background: accent }}
          >
            <MessageCircleQuestion size={16} /> <span className="hidden sm:inline">Ask Someone</span>
          </button>
          <button
            data-testid="change-password-btn"
            onClick={() => setShowPw(true)}
            className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] transition-colors"
          >
            <KeyRound size={16} /> <span className="hidden sm:inline">Password</span>
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#E07A5F] transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
      {showPw && <ChangePasswordModal mode="self" onClose={() => setShowPw(false)} />}
      {showAsk && <AskSomeoneModal onClose={() => setShowAsk(false)} />}
      <AnimatePresence>
        {showMsgs && <MessagesDrawer onClose={() => setShowMsgs(false)} onUnreadChange={setUnread} />}
      </AnimatePresence>
    </header>
  );
}
